// REST client wrappers for the Reports tab — FE-SCRM-058.

import { api } from "@/api/client";

import type {
  CompetitorListResponse,
  LossAnalysisResponse,
  OpportunityListResponse,
  ReferralSourcesResponse,
  ReportPeriod,
  VelocityResponse,
} from "./types";


export function fetchVelocityReport(opts: {
  signal?: AbortSignal;
  windowDays?: number;
}): Promise<VelocityResponse> {
  const sp = new URLSearchParams();
  if (opts.windowDays !== undefined) {
    sp.set("window_days", String(opts.windowDays));
  }
  const qs = sp.toString();
  return api.get<VelocityResponse>(
    `/api/v1/sales/reports/velocity${qs ? `?${qs}` : ""}`,
    { signal: opts.signal },
  );
}


export function fetchReferralSources(opts: {
  signal?: AbortSignal;
  period: ReportPeriod;
}): Promise<ReferralSourcesResponse> {
  const sp = new URLSearchParams({ period: opts.period });
  return api.get<ReferralSourcesResponse>(
    `/api/v1/sales/reports/referral-sources?${sp.toString()}`,
    { signal: opts.signal },
  );
}


export function fetchLossAnalysis(opts: {
  signal?: AbortSignal;
  period: ReportPeriod;
}): Promise<LossAnalysisResponse> {
  const sp = new URLSearchParams({ period: opts.period });
  return api.get<LossAnalysisResponse>(
    `/api/v1/sales/reports/loss-analysis?${sp.toString()}`,
    { signal: opts.signal },
  );
}


export function fetchCompetitors(opts: {
  signal?: AbortSignal;
  period: ReportPeriod;
}): Promise<CompetitorListResponse> {
  const sp = new URLSearchParams({ period: opts.period });
  return api.get<CompetitorListResponse>(
    `/api/v1/sales/competitors?${sp.toString()}`,
    { signal: opts.signal },
  );
}


// Competitor side panel client-filters the existing opportunities
// list endpoint by normalized incumbent_broker_firm. The list
// endpoint accepts ``?stage=`` for one stage at a time, so we run
// two requests in parallel — closed_won + closed_lost — and merge.
// No BE change in this chunk; a dedicated competitor-detail endpoint
// can replace this in v1.5 if perf becomes a concern.
export function fetchClosedOpportunities(
  opts?: { signal?: AbortSignal },
): Promise<OpportunityListResponse> {
  return Promise.all([
    api.get<OpportunityListResponse>(
      "/api/v1/sales/opportunities?stage=closed_won",
      { signal: opts?.signal },
    ),
    api.get<OpportunityListResponse>(
      "/api/v1/sales/opportunities?stage=closed_lost",
      { signal: opts?.signal },
    ),
  ]).then(([won, lost]) => ({
    rows: [...won.rows, ...lost.rows],
    total: won.total + lost.total,
  }));
}
